<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import slugify from 'slugify'

const showNameEditor = ref<boolean>(false)
const soundClip = ref<HTMLElement>()

const audioRef = ref<HTMLAudioElement>()

const props = defineProps<{
  audio: string
  modelValue: string
  defaultRecordingName: string
  showRenameFile: boolean
}>()

const emit = defineEmits<{
  (e: 'delete'): void
  (e: 'update:modelValue'): void
  (e: 'renameFile', filename: string): void
}>()

onMounted(() => audioRef.value.load())

watch(
  () => props.audio,
  () => audioRef.value.load()
)

const { t } = useI18n()
const { create } = useNotification()

const renameFile = () => {
  if (!recordingName.value) {
    create('error', {
      text: t('ui.soundPlayer.error.missingName'),
      timeout: 2000
    })
    return
  }
  showNameEditor.value = false
  emit('renameFile', slugify(recordingName.value, '-'))
}

const isDefaultName = computed(() => {
  return recordingName.value === props.defaultRecordingName
})

const recordingName = useVModel(props, 'modelValue', emit)
</script>

<template>
  <div ref="soundClip" class="rounded-md bg-muted p-5">
    <div class="flex flex-col items-center justify-between text-foreground md:flex-row">
      <div class="mb-2 flex flex-col items-center md:mb-0 md:flex-row">
        <audio ref="audioRef" preload="auto" controls class="m-auto max-h-full max-w-full object-contain">
          <source :src="audio" type="audio/mpeg" />
        </audio>
        <div class="mt-5 flex items-center px-5 md:mt-0">
          <p v-if="!showNameEditor" class="text-xs">{{ slugify(recordingName, '-') }}</p>
          <div v-else class="flex w-full flex-col items-baseline gap-1 md:flex-row">
            <AppFormInput
              id="title"
              v-model="recordingName"
              type="text"
              name="recordingName"
              @change="recordingName = slugify(recordingName, '-')"
            />
            <Button variant="outline" @click="renameFile">
              {{ t('global.words.save') }}
            </Button>
          </div>
          <BaseIcon
            v-if="showRenameFile && !showNameEditor"
            name="HeroPencilIconSolid"
            class="ml-1 h-3 w-3 cursor-pointer"
            @click="showNameEditor = true"
          />
        </div>
      </div>
      <BaseIcon
        v-if="!isDefaultName"
        name="HeroTrashIconSolid"
        class="h-5 w-5 cursor-pointer"
        @click="emit('delete')"
      />
    </div>
  </div>
</template>

<style scoped>
audio::-webkit-media-controls-enclosure {
  border-radius: 5px;
  background-color: #ffffff;
}
</style>
